import { useState, useEffect } from "react";
import { Plus, Trash2, Bell, CheckCircle, AlertCircle, Info, Megaphone } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const NOTIF_ICONS = {
  course: BookIcon,
  promo: Megaphone,
  system: AlertCircle,
  info: Info,
};

const NOTIF_COLORS = {
  course: "text-[#0284C7] bg-blue-50",
  promo: "text-[#D97706] bg-amber-50",
  system: "text-red-500 bg-red-50",
  info: "text-[#059669] bg-green-50",
};

function BookIcon(props) {
  return <Bell {...props} />;
}

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", notif_type: "info", target: "all" });

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notifications", form);
      setShowForm(false);
      setForm({ title: "", message: "", notif_type: "info", target: "all" });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this notification?")) {
      await api.delete(`/notifications/${id}`);
      fetchData();
    }
  };

  const markAsRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    fetchData();
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  const unread = notifications.filter(n => !n.read).length;

  return (
    <AdminLayout>
      <div data-testid="admin-notifications-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Notifications
            </h1>
            {unread > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5">
                {unread} unread
              </span>
            )}
          </div>
          <button
            data-testid="add-notification-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> New Notification
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-3" data-testid="notification-form">
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Title</label>
              <input data-testid="notif-title-input" placeholder="Notification title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Message</label>
              <textarea placeholder="Notification message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputClass} h-20`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Type</label>
                <select value={form.notif_type} onChange={(e) => setForm({ ...form, notif_type: e.target.value })} className={inputClass}>
                  <option value="info">Info</option>
                  <option value="course">Course</option>
                  <option value="promo">Promotion</option>
                  <option value="system">System</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Target</label>
                <select value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className={inputClass}>
                  <option value="all">All Users</option>
                  <option value="students">Students Only</option>
                  <option value="admin">Admins Only</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="notif-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-2.5 px-6 text-xs transition-none">
                Send Notification
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="admin-card border font-bold py-2.5 px-6 text-xs transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Notifications List */}
        <div className="space-y-2" data-testid="notifications-list">
          {notifications.map((notif) => {
            const colorClass = NOTIF_COLORS[notif.notif_type] || NOTIF_COLORS.info;
            const Icon = NOTIF_ICONS[notif.notif_type] || Info;
            return (
              <div key={notif.id} className={`admin-card border p-4 flex gap-3 ${notif.read ? "opacity-60" : ""}`}>
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-sm font-bold admin-text">{notif.title}</h3>
                    {!notif.read && <span className="w-2 h-2 bg-[#0284C7] flex-shrink-0" />}
                  </div>
                  <p className="text-xs admin-text-secondary leading-relaxed">{notif.message}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] admin-text-secondary">
                      {notif.created_at ? new Date(notif.created_at).toLocaleString() : "—"}
                    </span>
                    <span className="text-[10px] font-bold uppercase admin-text-secondary">{notif.target}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {!notif.read && (
                    <button onClick={() => markAsRead(notif.id)} className="p-1.5 text-green-500" title="Mark as read">
                      <CheckCircle size={14} />
                    </button>
                  )}
                  <button onClick={() => handleDelete(notif.id)} className="p-1.5 text-red-400" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          {notifications.length === 0 && <p className="text-sm admin-text-secondary py-8 text-center">No notifications</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
